import { FC, useEffect, useRef, useState } from "react";
import { useSelector } from 'react-redux';

import ChartComponent from "../../custom/chartComponet/chartComponent";

import { createFormatDateFunction } from '../../../utils/chartsData';

import '../../../components/charts/charts.scss';
import { formatDateForMapReport } from '../../../utils/formatDate';

interface IOptions {
  data: any;
  setPrintRef: (item: HTMLDivElement | null) => void;
};

const changesOptions: { [key: string]: string } = {
  contracts: 'Контракт',
  projects: 'Проекты',
  posIds: 'Торговая точка',
  retailChains: 'Сеть',
  city: 'Город',
  areas: 'Область',
  regions: 'Регионы',
  categorySku: 'Категории товаров',
  subCategorySku: 'Подкатегории товаров',
  groupSku: 'Группы товаров',
  subGroupSku: 'Подруппы товаров',
  skuIds: 'Товары',
  actionIds: 'Акции',
  rateStatuses: 'Статус оценки',
  reviewStatuses: 'Статус проверки',
  status: 'Статус',
  fromDate: 'Период'
};

const ScreenshotChartSection: FC<IOptions> = ({ data, setPrintRef }) => {
  const printRef = useRef<HTMLDivElement | null>(null);

  const {
    groupFilters,
    changeDataCharts,
  } = useSelector((state: any) => state.charts?.chartsList);

  const {
    filters,
    areasOptions,
    chainsOptions,
    regionsOptions,
    projectOptions,
    contractsOptions,
  } = useSelector((state: any) => state.userReports.userReports);

  const [isFiltersList, setIsFiltersList] = useState<string[]>([]);

  const axisX: { [key: string]: (data: string) => string } = {
    contracts: createFormatDateFunction(contractsOptions),
    projects: createFormatDateFunction(projectOptions),
    posIds: createFormatDateFunction(groupFilters.pos),
    retailChains: createFormatDateFunction(chainsOptions),
    areas: createFormatDateFunction(areasOptions),
    regions: createFormatDateFunction(regionsOptions),
    categorySku: createFormatDateFunction(groupFilters.category),
    subCategorySku: createFormatDateFunction(groupFilters.subcategory),
    groupSku: createFormatDateFunction(groupFilters.group),
    subGroupSku: createFormatDateFunction(groupFilters.subgroup),
    skuIds: createFormatDateFunction(groupFilters.sku),
    actionIds: createFormatDateFunction(groupFilters.action),
    rateStatuses: createFormatDateFunction(filters[0].rateStatuses),
    reviewStatuses: createFormatDateFunction(filters[0].reviewStatuses),
  };

  const getFiltersList = () => {
    const list: string[] = [];
    const keys = Object.keys(changeDataCharts.changes);
    const names = keys.map((key: string) => { return changesOptions[key] });
    const values: any[] = Object.values(changeDataCharts.changes);

    names.forEach((name: string, index: number) => {
      let newValues: string[];

      if (name === 'Город') {
        newValues = [values[index]];
      } else if (name === 'Статус') {
        if (values[0] === 'REVIEW_STATUS') {
          newValues = ['Статус провеки'];
        } else {
          newValues = ['Статус оценки'];
        }
      } else if (name === 'Период') {
        newValues = [`${formatDateForMapReport(changeDataCharts.changes.fromDate)}${changeDataCharts.changes.toDate ? ' - ' + formatDateForMapReport(changeDataCharts.changes.toDate) : ''}`]
      } else {
        if (Array.isArray(values[index])) {
          newValues = values[index].map((el: string) => {
            const key = keys[index];
            return axisX[key](el);
          });
        } else {
          return
        }
      }

      if (name && newValues.length) {
        list.push(`${name}: ${newValues.length > 1 ? newValues.join(', ') : newValues}.`);
      }
    })
    setIsFiltersList(list)
  };

  function getStatusValues(key: string) {
    if (data.labelsOptions) {
      return data.labelsOptions.map((status: { [key: string]: string }) => {
        return Object.keys(status)[0] === key ? Object.values(status)[0] : undefined;
      });
    } else {
      return key;
    }
  };

  useEffect(() => {
    setPrintRef(printRef.current);
    // eslint-disable-next-line
  }, [data, changeDataCharts]);

  useEffect(() => {
    if (changeDataCharts.hasChanged) {
      getFiltersList();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className='screenshot-section'>
      <div ref={printRef} className='content-charts'>
        <div className='charts__header'>
          <p className='charts__title'>{`${data.name}${data.isOn || data.isOn === 0 ? data.isOn ? ' (шт)' : ' (руб)' : ''}`}</p>
        </div>
        <div className='screenshot__chart'>
          {data.type !== ''
            ? <ChartComponent
              type={data.type}
              data={data.data}
              options={data.options}
              isHiddenItem={data.isHiddenItem}
              isIncreasedSize={false}
              loader={true}
              colorOptions={data.colorOptions}
              chartType={data.chartType}
            />
            :
            <div className='indicator'>
              <div className='indicator__block indicator__block_first'>
                <p className='indicator__header'>Кол-во отчётов, шт</p>
                <p>{data.data.dataReportsCharts.cheked + data.data.dataReportsCharts.noCheked}</p>
              </div>

              <div className='indicator__block indicator__block_second'>
                <p className='indicator__header'>Проверено, шт</p>
                <p>{data.data.dataReportsCharts?.cheked || 0}</p>
              </div>

              <div className='indicator__block indicator__block_third'>
                <p className='indicator__header'>Не проверено, шт</p>
                <p>{data.data.dataReportsCharts?.noCheked || 0}</p>
              </div>
            </div>}
        </div>
        <div className='screenshot-section__text'>
          {data.labels && data.labels.map((item: string, index: number) => {
            return (
              <div className='charts__item'
                key={index}
                style={{ display: `${data.isHiddenItem.includes(item) ? 'none' : 'auto'}` }}
              >
                {getStatusValues(item) &&
                  <div className='charts__color' style={{ backgroundColor: item ? `${data.colorOptions[index]}` : 'grey' }} />
                }
                {getStatusValues(item) &&
                  <p>{getStatusValues(item)}</p>
                }
              </div>)
          })}
        </div>
        {changeDataCharts.hasChanged && isFiltersList.map((item: string, index: number) => {
          return (
            <div
              key={index}
              className='screenshot-section__text'>
              <p>{item}</p>
            </div>
          )
        })}
      </div>
    </div >
  );
};

export default ScreenshotChartSection;
